import ConversationItemDtoConverter from 'scripts/application/dto_converters/conversation_item_converter';
import CreateToast from 'scripts/application/actions/toast/create_toast';
import ErrorReporter from 'scripts/infrastructure/error_reporter';
import { getErrorToastParams, translateText } from 'scripts/application/actions/lib/ai_translate';
import Language from 'models/language';
import qconsole from 'scripts/lib/qconsole';
import Translation from 'scripts/domain/models/translation';
import TranslationRequest from 'scripts/domain/models/translation_request';

export default class TranslateConversationItem {
  constructor(context) {
    this.context = context;
  }

  run(params) {
    const { conversationItem, targetLanguage, customerId, onComplete } = params;

    if (!targetLanguage) {
      qconsole.error('Target language not specified');
      onComplete?.();
      return;
    }

    this.context.gateways.currentAgentProfile.update({ language: targetLanguage });

    // Supernova doesn't broadcast every conversation item update, such as SLA
    // changes, which would create a log of broadcast spam just to handle
    // edge-cases. In this scenario, our local version is out-of-sync and any
    // PATCH fails because the version isn't up-to-date. When we encounter this
    // rare scenario, we simply retrieve the latest version of the conversation
    // item and retry -- it's a low risk for races because we're only updating a
    // translation, so in the rare event we get a race, the impact is very
    // small.
    return attemptTranslation(this.context, conversationItem, targetLanguage, customerId)
      .catch(initialError => {
        if (!isVersionConflictError(initialError)) {
          qconsole.log('translate: no version conflict', initialError);
          const toastParams = getErrorToastParams(initialError);
          this.context.executeAction(CreateToast, toastParams);
          return;
        }

        // Re-retrieve the latest version and try again.
        qconsole.log('translate: version conflict', initialError);
        return this.context.gateways.customerHistory
          .fetchConversationItem(customerId, conversationItem.id)
          .then(latestItemDto => {
            const latestConversationItem = ConversationItemDtoConverter.fromDto(latestItemDto);
            return attemptTranslation(this.context, latestConversationItem, targetLanguage, customerId);
          })
          .catch(fetchOrRetryError => {
            const toastParams = getErrorToastParams(fetchOrRetryError);
            this.context.executeAction(CreateToast, toastParams);
          });
      })
      .finally(() => {
        onComplete?.();
      });
  }
}

function attemptTranslation(context, itemToTranslate, targetLanguage, customerId) {
  const { conversationHistory, customerLanguage } = context.stores.customers.storesFor(customerId);

  const text = itemToTranslate.content.getMessageText();
  const translationRequest = TranslationRequest.create({
    conversationId: itemToTranslate.conversationId,
    conversationItemId: itemToTranslate.id,
    targetLanguage,
    text,
  });

  return translateText(customerId, context, translationRequest)
    .then(responseDto => {
      qconsole.log('translate: responseDto', responseDto);
      if (!responseDto) {
        return undefined;
      }

      const translation = Translation.create({
        content: responseDto.text,
        contentLanguage: responseDto.targetLanguage,
        parentLanguage: responseDto.sourceLanguage,
        model: responseDto.model,
        provider: responseDto.provider,
        translatedAt: responseDto.translatedAt,
      });

      customerLanguage.set(Language.create({ id: 'customer-language', code: responseDto.sourceLanguage }));

      itemToTranslate.replaceTranslation(translation);
      itemToTranslate.incrementVersion();
      conversationHistory.addOrReplace(itemToTranslate);

      return context.gateways.customerHistory
        .updateConversationItem(customerId, itemToTranslate.id, ConversationItemDtoConverter.toDto(itemToTranslate))
        .then(() => {
          context.analytics.track('Conversation Item Translated', {
            customerId,
            conversationId: itemToTranslate.conversationId,
            conversationItemId: itemToTranslate.id,
            sourceLanguage: responseDto.sourceLanguage,
            targetLanguage: responseDto.targetLanguage,
            model: responseDto.model,
            provider: responseDto.provider,
          });
          qconsole.log('translate: succeeded', translation);
          return translation;
        });
    })
    .catch(error => {
      ErrorReporter.reportError(error);
      throw error;
    });
}

function isVersionConflictError(errorObj) {
  return errorObj?.errors?.[0]?.code === 'version_outdated';
}
